import React, { useState, useEffect, useMemo, useRef } from "react";
import { roundTo, applyCurrencyRounding, getRoundingMethod } from "../../utils/tillHelpers";
import { Drawer, Input, Row, Col, Button, Modal, message } from "antd";
import { CloseOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import DeleteIcon from "../../assets/images/delete1.svg";
import { parse, v4 as uuidv4 } from "uuid";
import BarCodeGroupedHandler from "../BarcodeGroupedHandler";
import IntegratedWeightModal from "./IntegratedWeightModal";
import { useEventBus } from "./EventBusProvider";
import { Scrollbars } from "react-custom-scrollbars";
import { needsPopup } from "../hooks/productPopupUtils";
import { numberToWords } from "../../utils/numberToWords";
import { pricingRuleController } from "../../../src/components/PointOfSale/Core/PricingRules/pricingRuleController";
import db from "../../database";
import { waitForProductsTableStable } from "../../utils/waitForProductsSync";
import { getPricingRules, findPricingRuleById } from "../../utils/pricingRulesCache";
import dayjs from "dayjs";
import { stock } from "../../../src/components/PointOfSale/Core/stock.js";
import { CheckoutTotalManualDiscount } from "../../components/PointOfSale/Core/PricingRules/CheckoutTotalManualDiscount.js";
import { CheckoutFlatDiscount } from "../../components/PointOfSale/Core/PricingRules/CheckoutFlatDiscount.js";
import { CheckoutPercentageDiscount } from "../../components/PointOfSale/Core/PricingRules/CheckoutPercentageDiscount.js";
import messages from "../../errors/messages.en-IN.json";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useCartStore from "../../stores/useCartStore";
import useAdvanceOrderStore from "../../stores/useAdvanceOrderStore";
import { getIsInterState } from "../../utils/taxUtils";
import { groupItemsByTaxRate } from "../../components/PointOfSale/Core/utils/taxCalculations";

const GroupedProducts = (props) => {
  const {
    showCustomMix,
    setShowCustomMix,
    cart,
    setCart,
    customMixItems,
    setCustomMixItems,
    groupName,
    setGroupName,
    groupQty,
    setGroupQty,
    existingGroupItems,
    setExistingGroupItems,
    selectedCustomMix,
    setSelectedCustomMix,
    orderType,
    setOrderType,
    posSaleTypes,
    setPosSaleTypes,
    currenciesData,
    setCurrenciesData,
    searchInputRef,
    setActiveInput,
    setProductStock,
    setShowCompleteSaleStock,
    setCompleteSaleStockData,
    processBillDiscounts,
    setLoader,
    processTotalManualDiscount,
  } = props;
  const itemsPerPage = 10;
  const [productData, setProductData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductSelection, setShowProductSelection] = React.useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Ref is the real double-submit guard; the state only drives the button UI.
  // setSubmitting does not update the captured closure synchronously.
  const submittingRef = useRef(false);
  const [manualDiscountTypes, setManualDiscountTypes] = useState([]);
  const tillData = useAuthStore.getState().tillData;
  const posConfig = usePOSConfigStore.getState().posConfig;
  const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
  let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
  const precision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const { dispatchWeight } = useEventBus();

  // --- Integrated weight popup state (ProductsPopup-style -- for products WITHOUT alternate UOM) ---
  const [integratedWeightProduct, setIntegratedWeightProduct] = useState(null);
  const [integratedWeightPopupOpen, setIntegratedWeightPopupOpen] = useState(false);
  const integratedWeightCallbackRef = useRef(null);
  const integratedWeightInputRef = useRef(null);
  const isAdvanceOrderEnabled = useAdvanceOrderStore.getState().isAdvanceOrderEnabled;
  const isPreOrderPage = window.location.pathname.includes("/preorder");

  const openWeightPopupForGrouped = (product, callback) => {
    const existing = customMixItems.find((i) => i.mProductId === product.mProductId && i.uom === (product.uom || product.csUomId));
    const initWeight = existing?.weight || product.weight || 0;
    const initRaw = existing?.rawWeight || product.rawWeight || initWeight.toString();
    const hasAlternateUom = product?.alternateUom?.length > 0 && product?.showAlternateUomInPos === "Y";

    if (hasAlternateUom) {
      const productWithInit = { ...product, weight: initWeight, rawWeight: initRaw };
      dispatchWeight(productWithInit, () => {}, null, () => {}, (updatedProduct) => {
        if (updatedProduct) callback(updatedProduct);
      });
    } else {
      setIntegratedWeightProduct({ ...product, weight: initWeight, rawWeight: initRaw });
      setIntegratedWeightPopupOpen(true);
      integratedWeightCallbackRef.current = callback;
      setTimeout(() => integratedWeightInputRef.current?.focus?.(), 100);
    }
  };

  // --- Integrated weight popup handlers (ProductsPopup-style) ---
  const handleIntegratedWeightTotalQty = (value) => {
    if (!integratedWeightProduct) return;
    const currentWeight = integratedWeightProduct.weight?.toString() || "0";
    let newWeight;
    if (value === "x") {
      newWeight = currentWeight.slice(0, -1) || "0";
    } else if (value === "clear") {
      newWeight = "0";
    } else {
      newWeight = currentWeight === "0" ? value : currentWeight + value;
    }
    setIntegratedWeightProduct({ ...integratedWeightProduct, weight: newWeight, rawWeight: newWeight });
  };

  const handleIntegratedWeightInputChange = (value) => {
    if (!integratedWeightProduct) return;
    const cleaned = value.replace(/[^0-9.]/g, "");
    setIntegratedWeightProduct({ ...integratedWeightProduct, weight: cleaned, rawWeight: cleaned });
  };

  const handleIntegratedWeightConfirm = () => {
    if (!integratedWeightProduct) return;
    const isStrictQty = posConfig?.requireQtyBeforeAddingItem === "Y";
    if (isStrictQty && Number(integratedWeightProduct.weight || 0) <= 0) return;
    const updatedProduct = {
      ...integratedWeightProduct,
      weight: isStrictQty ? integratedWeightProduct.weight : (Number(integratedWeightProduct.weight) === 0 ? "1" : integratedWeightProduct.weight),
      rawWeight: integratedWeightProduct.rawWeight || integratedWeightProduct.weight?.toString(),
      unit: integratedWeightProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    };
    integratedWeightCallbackRef.current?.(updatedProduct);
    setIntegratedWeightProduct(null);
    setIntegratedWeightPopupOpen(false);
    integratedWeightCallbackRef.current = null;
  };

  const handleIntegratedWeightCancel = () => {
    integratedWeightCallbackRef.current?.(null);
    setIntegratedWeightProduct(null);
    setIntegratedWeightPopupOpen(false);
    integratedWeightCallbackRef.current = null;
  };

  useEffect(() => {
    (async () => {
      // Guard against reading db.products while a Full Sync has cleared it and
      // is still repopulating in the background — see waitForProductsSync.js.
      await waitForProductsTableStable();

      // CW Browser (SQLite) note: .filter() on a DB collection cannot carry a
      // JS closure over IPC — the function gets toString()'d and re-eval'd on
      // the main process, dropping captured variables. Always toArray() first
      // and filter in plain JS so behavior is identical in Web + Electron.
      const allProducts = await db.products.toArray();
      const productsList = allProducts.filter(
        (product) => product.isactive == true && product.productSegment !== "BG"
      );
      setProductData(productsList);
    })();
  }, []);

  useEffect(() => {
    const pr = getPricingRules();
    const bunitId = tillData.tillAccess.csBunit.csBunitId;
    const manualPricingRules = pr.filter((rule) => {
      if (rule.manualDiscount === "Y") {
        if (rule.excludeBusinessUnits === "Y") {
          if (rule.mPricingBUnits.length === 0) return rule;
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId !== bunitId);
          if (index !== -1) {
            return rule;
          }
        } else {
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId === bunitId);
          if (index >= 0) {
            return rule;
          }
        }
      }
    });
    setManualDiscountTypes([...manualPricingRules]);
  }, []);

  useEffect(() => {
    let filtered = productData.filter((item) => {
      const lowerQuery = searchQuery.trim().toLowerCase();
      const isValidSegment = item.productSegment !== "CB";
      const matchesSearch =
        item.name.toLowerCase().includes(lowerQuery) || item.value.toLowerCase().includes(lowerQuery) || (item.upc && item.upc.toLowerCase().includes(lowerQuery));
      return isValidSegment && matchesSearch;
    });

    // filtered.sort((a, b) => {
    //   const aInCart = customMixItems.find((item) => item.mProductId === a.mProductId);
    //   const bInCart = customMixItems.find((item) => item.mProductId === b.mProductId);
    //   const aQty = aInCart ? aInCart.weight : 0;
    //   const bQty = bInCart ? bInCart.weight : 0;
    //   return (bQty > 1) - (aQty > 1);
    // });

    setFilteredProducts(filtered);
  }, [searchQuery, productData, customMixItems]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleIncrease = async (product) => {
    if (needsPopup(product)) {
      openWeightPopupForGrouped(product, async (updatedProduct) => {
        let newValue = updatedProduct?.rawWeight ?? updatedProduct?.weight?.toString() ?? "";

        // 👉 if user clicked confirm without entering anything
        if (!newValue || newValue === "0") {
          newValue = "1";
        }

        await handleQuantityChange(updatedProduct, newValue);
      });
      return;
    }
    const isInCart = customMixItems.find((item) => item.mProductId === product.mProductId && item.uom === (product.uom || product.csUomId));
    const prevMixItems = [...customMixItems];
    const updatedMixItems = isInCart
      ? customMixItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: Number(item.weight) + 1,
                rawWeight: (Number(item.weight) + 1).toString(),
              }
            : item
        )
      : [
          ...customMixItems,
          {
            ...product,
            weight: 1,
            rawWeight: "1",
            uom: product.uom || product.csUomId,
            lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          },
        ];
    const tempCart = buildMergedCartForStock(updatedMixItems);
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      let isFinalValidation = false;
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
      });

      if (!stockCheckResult) {
        setProductStock(true);
        setCustomMixItems(prevMixItems);
        return;
      }
    }
    setCustomMixItems(updatedMixItems);
  };

  // const handleIncrease = (product) => {
  //   openWeightPopupForGrouped(product, async (updatedProduct) => {
  //     const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString();

  //     await handleQuantityChange(product, newValue);
  //   });
  // };

  const handleDecrease = (product) => {
    if (needsPopup(product)) {
      openWeightPopupForGrouped(product, async (updatedProduct) => {
        const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString();

        await handleQuantityChange(updatedProduct, newValue);
      });
      return;
    }
    const isInCart = customMixItems.find((item) => item.mProductId === product.mProductId && item.uom === (product.uom || product.csUomId));
    if (isInCart && isInCart.weight > 1) {
      setCustomMixItems(
        customMixItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: Number(item.weight) - 1,
                rawWeight: (Number(item.weight) - 1).toString(),
              }
            : item
        )
      );
    } else if (isInCart && isInCart.weight <= 1) {
      setCustomMixItems(customMixItems.filter((item) => item.mProductId !== product.mProductId));
    }
  };

  // const handleDecrease = (product) => {
  //   openWeightPopupForGrouped(product, async (updatedProduct) => {
  //     const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString();

  //     await handleQuantityChange(product, newValue);
  //   });
  // };

  const handleDelete = (itemToDelete) => {
    setCustomMixItems(customMixItems.filter((item) => item.lineId !== itemToDelete.lineId));
  };

  const handleQuantityChange = async (product, value) => {
    const isDecimal = product?.uomData?.[0]?.decimal === "Y";
    let newValue = value;

    if (newValue === "") {
      newValue = "0";
    }

    const currentWeight = product.rawWeight || (product.weight !== undefined ? product.weight.toString() : "0");
    const isInitialZero = currentWeight === "0";

    if (isInitialZero && /\d/.test(newValue) && !newValue.startsWith("0.")) {
      newValue = newValue.replace(/^0+/, "");
      if (newValue === "") newValue = "0";
    }

    const isValidInput = isDecimal ? /^(\d*\.?\d{0,3}|\d*\.|\d*\.\d{0,3}\.)$/.test(newValue) : /^\d*$/.test(newValue);
    if (!isValidInput) return;

    const parsedValue = newValue === "0" || newValue === "." || newValue === "0." || newValue === "0.0" || newValue === "0.00" ? 0 : parseFloat(newValue) || 0;

    if (parsedValue < 0) {
      message.error(`${messages.info.quantity_not_negative}`);
      return;
    }

    const isInCart = customMixItems.find((item) => item.mProductId === product.mProductId && item.uom === (product.uom || product.csUomId));
    const prevMixItems = [...customMixItems];
    let updatedMixItems = [];

    if (isInCart) {
      if (parsedValue === 0 && ![".", "0.", "0.0", "0.00"].includes(newValue)) {
        updatedMixItems = customMixItems.filter((item) => item.mProductId !== product.mProductId);
      } else {
        updatedMixItems = customMixItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: isDecimal ? parsedValue : Math.floor(parsedValue),
                rawWeight: newValue,
              }
            : item
        );
      }
    } else if (parsedValue > 0 || [".", "0.", "0.0", "0.00"].includes(newValue)) {
      updatedMixItems = [
        ...customMixItems,
        {
          ...product,
          weight: isDecimal ? parsedValue : Math.floor(parsedValue),
          rawWeight: newValue,
          uom: product.uom || product.csUomId,
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
        },
      ];
    } else {
      return;
    }
    const tempCart = buildMergedCartForStock(updatedMixItems);
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation: false,
      });
      if (!stockCheckResult) {
        setCustomMixItems(prevMixItems);
        return;
      }
    }
    setCustomMixItems(updatedMixItems);
  };

  useEffect(() => {
    const totalRecords = 12;
    let filledCurrencies = [...currenciesData];
    while (filledCurrencies.length < totalRecords) {
      filledCurrencies.push({ id: `placeholder-${filledCurrencies.length}`, value: 999999 });
    }
    setCurrenciesData(filledCurrencies.sort((a, b) => b.value - a.value));
    db.posSaletypes.toArray().then((saleType) => {
      setPosSaleTypes([...saleType]);
      const saleIndex = saleType.findIndex((st) => st.cwrSaletype.isdefault === "Y");
      setOrderType(saleType[saleIndex]);
    });
  }, []);

  const buildMergedCartForStock = (updatedMixItems) => {
    const grouped = {};

    // Flatten existing cart items (expand children), but SKIP the combo being
    // edited so its children don't double-count with the new items (#13)
    const editingLineId = existingGroupItems ? selectedCustomMix?.lineId : null;
    (cart?.items || []).forEach((item) => {
      if (editingLineId && item.lineId === editingLineId) return; // skip combo being edited
      if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => {
          const key = `${child.productId || child.mProductId}_${child.mBatchId || "null"}`;
          if (!grouped[key]) {
            grouped[key] = { ...child, weight: Number(child.weight) };
          } else {
            grouped[key].weight += Number(child.weight);
          }
        });
      } else {
        const key = `${item.productId || item.mProductId}_${item.mBatchId || "null"}`;
        if (!grouped[key]) {
          grouped[key] = { ...item, weight: Number(item.weight) };
        } else {
          grouped[key].weight += Number(item.weight);
        }
      }
    });

    // Merge new custom mix items on top
    updatedMixItems.forEach((item) => {
      const key = `${item.mProductId}_${item.mBatchId || "null"}`;
      if (!grouped[key]) {
        grouped[key] = { ...item, productId: item.mProductId, weight: Number(item.weight) };
      } else {
        grouped[key].weight += Number(item.weight);
      }
    });

    return {
      ...cart,
      items: Object.values(grouped),
    };
  };

  const handleSubmit = async () => {
    if (submittingRef.current || submitting) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (customMixItems.length === 0) {
        message.warning("Please add at least one item to the combo before submitting");
        return;
      }
      if (customMixItems.length > 0) {
        if (!groupName) {
          message.warning(`${messages.info.groupname_required}`);
          return;
        }
        // Validate at least one item has qty > 0
        const hasValidItem = customMixItems.some((item) => Number(item.weight) > 0);
        if (!hasValidItem) {
          message.warning("Please add at least one item with quantity greater than 0");
          return;
        }
        const cartObj = cart || { items: [], total: 0, tax: 0, subTotal: 0 };
        const itemSummery = {
          decimalItems: { totalQty: 0, totalAmount: 0 },
          nonDecimalItems: { totalQty: 0, totalAmount: 0 },
          itemsQty: 0,
          items: 0,
        };
        const prevSummary = cartObj.itemsSummery;
        const totalTaxAmount = customMixItems.reduce((sum, item) => {
          const taxAmount =
            taxIncludeFlag === "Y"
              ? (item.sunitprice - item.sunitprice / (1 + item.taxRate / 100)) * (groupQty || 1) * item.weight
              : item.sunitprice * (item.taxRate / 100) * (groupQty || 1) * item.weight;
          return sum + taxAmount;
        }, 0);

        const totalRealPrice = customMixItems.reduce((sum, item) => {
          return sum + (item.sunitprice || 0);
        }, 0);

        const totalAmount = customMixItems.reduce((sum, item) => {
          return sum + item.sunitprice * (groupQty || 1);
        }, 0);

        const cartTotal = customMixItems.reduce((sum, item) => {
          const sp = parseFloat(item.sunitprice);
          const mrp = sp * item.weight * groupQty;
          const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + item.taxRate / 100) : (mrp * item.taxRate) / 100;
          const nettotal = parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax);
          const nettotalFinal = parseFloat(parseFloat(nettotal).toFixed(precision));
          return sum + (nettotalFinal || 0);
        }, 0);
        const childrenID = uuidv4().replace(/-/g, "").toUpperCase();
        let obj = {
          addNewLine: "N",
          batchedForSale: "N",
          batchedForStock: "N",
          batchedProduct: "N",
          batchno: null,
          bunitName: "",
          csBunitId: null,
          customAttributes: [],
          cwrMenuTimeslot: [],
          cwrPcatalogueId: "75A987950FAC4571985E4FDAADC1366F",
          description: "",
          discount: 0,
          discountName: "",
          foodType: "",
          itemType: "",
          grosslist: 0,
          grossstd: 0,
          grossunit: 0,
          hsncode: "",
          imageUrl: "",
          imageurl: "",
          isDecimal: false,
          isDecimalQty: false,
          isManualQty: false,
          restrictPosQtyEdit: "N",
          isPromoApplicable: false,
          // Parent custom-mix item is synthetic (no own UOM); children carry actual precision
          isQtyDesimal: 2,
          isReturn: false,
          key: 0,
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          childrenId: childrenID,
          linegross: 0,
          linenet: 0,
          linetax: 0,
          listPrice: 0,
          listprice: 0,
          mBatchId: null,
          mPricingruleId: null,
          multiPrice: "N",
          name: groupName,
          name2: "",
          netList: 0,
          netStd: 0,
          nettotal: cartTotal,
          netunit: 0,
          newCustomAttributes: [],
          order: "N",
          originalPrice: totalRealPrice,
          priceList: [],
          primaryOrderLine: null,
          productAddons: [],
          productCategoryName: "",
          productId: uuidv4().replace(/-/g, "").toUpperCase(),
          productManufacturerId: null,
          productionCenter: "",
          realPrice: totalRealPrice,
          returnQty: null,
          salePrice: 0,
          salesRepId: null,
          salesRepName: "",
          selectedAddons: [],
          shelfLife: "",
          shortDescription: "",
          showAddons: "N",
          sunitprice: cartTotal,
          taxAmount: parseFloat(parseFloat(totalTaxAmount).toFixed(precision)),
          taxCategory: "",
          taxFlag: "N",
          taxName: "",
          type: "I",
          unitPrice: 0,
          unitTax: 0,
          upc: "",
          upcPricingRule: null,
          value: "",
          weight: groupQty,
          children: customMixItems.map((item) => {
            const sp = parseFloat(item.sunitprice);
            const mrp = sp * item.weight * (groupQty || 1);
            const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + item.taxRate / 100) : (mrp * item.taxRate) / 100;
            const nettotalFinal = parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax);
            const nettotalFixed = parseFloat(nettotalFinal);
            const taxAmountItem = taxIncludeFlag === "Y" ? item.sunitprice - item.sunitprice / (1 + item.taxRate / 100) : item.sunitprice * (item.taxRate / 100);
            const taxAmountFixed = parseFloat(parseFloat(taxAmountItem * (groupQty || 1)).toFixed(precision));
            const unitPrice = isFinite(nettotalFinal / (item.weight * (groupQty || 1)))
              ? taxIncludeFlag === "Y"
                ? nettotalFinal / (item.weight * (groupQty || 1)) / (1 + item.taxRate / 100)
                : item?.sunitprice
              : 0;
            const grossUnit = item.sunitprice / item.weight;
            const lineTax = item.slistprice * (1 + item.taxRate / 100);
            const stdTax = item.sunitprice * (1 + item.taxRate / 100);
            const gross_list = taxIncludeFlag === "Y" ? item.slistprice : parseFloat(parseFloat(lineTax).toFixed(precision));
            let netStd = parseFloat(item.sunitprice - item.sunitprice / (1 + item.taxRate / 100));
            const netStdItem = taxIncludeFlag === "Y" ? (item.sunitprice > 0 ? parseFloat(parseFloat(item.sunitprice - netStd).toFixed(precision)) : 0) : (item.sunitprice ?? 0);
            const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : stdTax;
            const netList =
              taxIncludeFlag === "Y" ? item.slistprice - parseFloat(parseFloat(item.slistprice - item.slistprice / (1 + item.taxRate / 100)).toFixed(precision)) : item.slistprice;
            return {
              ...item,
              productId: item.mProductId,
              lineId: uuidv4().replace(/-/g, "").toUpperCase(),
              childrenId: childrenID,
              groupedItems: true,
              discount: 0,
              tax: item.cTaxId,
              nettotal: parseFloat(parseFloat(nettotalFinal).toFixed(precision)),
              realPrice: item.sunitprice || 0,
              weight: parseFloat((item.weight * groupQty).toFixed(item?.uomData?.[0]?.stdprecision ?? 2)),
              rawWeight: item.rawWeight,
              isQtyDesimal: item?.uomData?.[0]?.stdprecision ?? 2,
              isDecimalQty: item?.uomData?.[0]?.decimal === "Y" ? true : false,
              taxAmount: parseFloat(parseFloat(tax).toFixed(precision)),
              linetax: parseFloat(parseFloat(tax).toFixed(precision)),
              linenet: nettotalFixed
                ? parseFloat((parseFloat(parseFloat(nettotalFixed).toFixed(precision)) - parseFloat(parseFloat(tax).toFixed(precision))).toFixed(precision))
                : 0,
              linegross: parseFloat(parseFloat(nettotalFixed).toFixed(precision)) || 0,
              linestd: nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight).toFixed(precision)) : 0,
              netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice).toFixed(precision)) : 0,
              listPrice: nettotalFixed ? item.slistprice : 0,
              grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice).toFixed(precision)) * (item.isReturn ? -1 : 1) : 0,
              grosslist: nettotalFixed ? gross_list : 0,
              grossstd: nettotalFixed ? parseFloat(parseFloat(grossStd).toFixed(precision)) : 0,
              netStd: netStdItem,
              netList: nettotalFixed ? parseFloat(parseFloat(netList).toFixed(precision)) : 0,
              actualWeight: item.weight * (groupQty || 1),
              baseQty: item.weight,
              mBatchId: null,
            };
          }),
        };

        let updatedItems = [...cartObj.items];

        if (existingGroupItems) {
          const existingGroupIndex = updatedItems.findIndex((item) => item.lineId === selectedCustomMix.lineId);
          if (existingGroupIndex !== -1) {
            updatedItems[existingGroupIndex] = obj;
          } else {
            updatedItems.push(obj);
          }
        } else {
          updatedItems.push(obj);
        }

        if (cart.couponInput?.length > 0) {
          cart.couponInput = cart.couponInput?.filter((coupon) => {
            if (obj.couponInput !== coupon) {
              return coupon;
            }
          });
        }

        // const allChildren = (updatedItems || []).flatMap((item) => (Array.isArray(item.children) ? item.children : []));
        const isInterState = getIsInterState(cart);
        const groupedItemsByTaxRate = groupItemsByTaxRate(updatedItems || [], precision, { isInterState });

        customMixItems.forEach((item) => {
          if (Array.isArray(item.children) && item.children.length > 0) {
            item.children.forEach((child) => {
              const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
              const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
              const itemWeight = child.weight * (groupQty || 1);
              const netTotal = parseFloat(parseFloat(child.sunitprice * itemWeight).toFixed(precision));

              itemSummery.itemsQty += itemWeight;
              itemSummery.items += 1;
              itemSummery[summaryKey].totalQty += itemWeight;
              itemSummery[summaryKey].totalAmount += netTotal;
            });
          } else {
            const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
            const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
            const itemWeight = item.weight * (groupQty || 1);
            const netTotal = parseFloat(parseFloat(item.sunitprice * itemWeight).toFixed(precision));

            itemSummery.itemsQty += itemWeight;
            itemSummery.items += 1;
            itemSummery[summaryKey].totalQty += itemWeight;
            itemSummery[summaryKey].totalAmount += netTotal;
          }
        });

        const mergedItemSummery = {
          decimalItems: {
            totalQty: (prevSummary.decimalItems?.totalQty || 0) + itemSummery.decimalItems.totalQty,
            totalAmount: (prevSummary.decimalItems?.totalAmount || 0) + itemSummery.decimalItems.totalAmount,
          },
          nonDecimalItems: {
            totalQty: (prevSummary.nonDecimalItems?.totalQty || 0) + itemSummery.nonDecimalItems.totalQty,
            totalAmount: (prevSummary.nonDecimalItems?.totalAmount || 0) + itemSummery.nonDecimalItems.totalAmount,
          },
          itemsQty: (prevSummary?.itemsQty || 0) + itemSummery.itemsQty,
          items: (prevSummary?.items || 0) + itemSummery.items,
        };

        const totalSTD = cartObj.items.reduce((total, item) => {
          const calculateLineSTD = (data) => {
            const sp = parseFloat(data.sunitprice);
            const mrp = sp * data.weight * (groupQty || 1);
            const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + data.taxRate / 100) : (mrp * data.taxRate) / 100;
            const nettotalFinal = parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax);
            const nettotalFixed = parseFloat(nettotalFinal);
            const stdTax = data.sunitprice * (1 + data.taxRate / 100);
            const grossStd = taxIncludeFlag === "Y" ? data.sunitprice : stdTax;

            const lineSTD = nettotalFixed ? grossStd * data.weight : 0;

            return lineSTD;
          };

          let itemTotalSTD = 0;

          if (Array.isArray(item.children)) {
            itemTotalSTD = item.children.reduce((childTotal, child) => {
              return childTotal + calculateLineSTD(child);
            }, 0);
          } else {
            itemTotalSTD = calculateLineSTD(item);
          }

          return total + itemTotalSTD;
        }, 0);
        // BUG-2103: this grouped-product cart was assembled by raw .reduce() and never went
        // through applyCurrencyRounding at all, so a float-accumulated total (and no roundOff)
        // went straight onto the cart. Carried over unfixed from the 2026-08-13 TSK-2357
        // rollout audit; brought in line with cart.js here.
        const rawGroupedTotal = updatedItems.reduce((sum, item) => sum + (item.nettotal || 0), 0);
        const { roundedTotal: finalCartTotal, roundOffAmount: finalCartRoundOff } = applyCurrencyRounding(rawGroupedTotal, getRoundingMethod(tillData), precision);
        const finalCartTax = updatedItems.reduce((sum, item) => sum + (parseFloat(parseFloat(item.taxAmount).toFixed(precision)) || 0), 0);
        const finalCartSubTotal = roundTo(finalCartTotal - finalCartTax, precision);
        const totalLineStd = customMixItems.reduce((sum, item) => {
          const stdTax = item.sunitprice * (1 + (item.taxRate || 0) / 100);
          const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : stdTax;
          const lineStdRaw = grossStd * item.weight;
          const lineStd = parseFloat(parseFloat(lineStdRaw).toFixed(precision));
          return sum + lineStd;
        }, 0);
        let finalCartObj = {
          ...cartObj,
          amountInWords: numberToWords(finalCartTotal),
          items: updatedItems,
          total: finalCartTotal,
          totalQty: groupQty,
          tax: roundTo(finalCartTax, precision),
          roundOff: finalCartRoundOff,
          subTotal: finalCartSubTotal,
          groupItemsFlag: true,
          totalStd: parseFloat(parseFloat(totalSTD + totalLineStd).toFixed(precision)),
          groupedItemsByTaxRate: groupedItemsByTaxRate,
          itemsSummery: mergedItemSummery,
        };
        // setCart(finalCartObj);
        let updatedCart;
        if (finalCartObj.items?.length > 0) {
          let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
          orderTimeDetails = {
            ...orderTimeDetails,
            paymentStartTime: "",
            orderStartTime: orderTimeDetails.orderStartTime !== "" ? orderTimeDetails.orderStartTime : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
          };
          usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
          updatedCart = obj.isReturn ? finalCartObj : await pricingRuleController(obj, finalCartObj, cart, setCart);
        }
        if (updatedCart?.couponInput?.length > 0 && cart?.statusType !== "layAway") {
          await Promise.all(
            updatedCart.couponInput.map(async (coupon) => {
              updatedCart = await pricingRuleController(
                obj,
                updatedCart,
                cart,
                setCart,
                null,
                orderType,
                true,
                coupon.couponCode,
                coupon.referenceId,
                coupon.mPricingCouponId,
                coupon.mPricingruleId
              );
            })
          );
        }
        const allDiscounts = [...(updatedCart?.manualDiscountData?.lineLevelDiscount || []), ...(updatedCart?.manualDiscountData?.totalLevelDiscount || [])].sort(
          (a, b) => a.sequenceNo - b.sequenceNo
        );

        const uniqueDiscounts = allDiscounts.reduce((acc, item) => {
          if (!acc.some((discount) => discount.pricingRule === item.pricingRule)) {
            acc.push(item);
          }
          return acc;
        }, []);

        uniqueDiscounts.forEach(async (item) => {
          const pricingRule = manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule);

          if (pricingRule) {
            if (pricingRule.type === "FD" || pricingRule.type === "PD") {
              updatedCart.items.forEach(async (cartItem) => {
                const isLineLevelDiscount = updatedCart?.manualDiscountData?.lineLevelDiscount?.some((d) => d.pricingRule === item.pricingRule);
                let allDiscounts = cartItem?.allDiscounts || [];
                let matchedRuleIndex = allDiscounts.findIndex((d) => d.mPricingruleId === pricingRule.mPricingrulesId);
                if (isLineLevelDiscount && matchedRuleIndex !== -1) {
                  const applyDiscount = pricingRule.type === "FD" ? CheckoutFlatDiscount : CheckoutPercentageDiscount;
                  cartItem.priority = 0;
                  // TIC-10890: `item` is deduped by rule, so take this line's own entry for its approver.
                  const lineEntry = updatedCart?.manualDiscountData?.lineLevelDiscount?.find((d) => d.lineId === cartItem.lineId && d.pricingRule === item.pricingRule);
                  updatedCart = await applyDiscount(cartItem, pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue, lineEntry?.approver);
                }
              });
            }

            if (pricingRule.type === "TD") {
              await CheckoutTotalManualDiscount(pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue, item.approver);
            }
          }
        });
        if (cart.totalBillDicount && typeof processBillDiscounts === "function") {
          const matchingPricingRules = findPricingRuleById(cart.totalBillDicount);
          await processBillDiscounts(matchingPricingRules[0], cartObj, true);
          updatedCart = useCartStore.getState().cart;
        }

        if (cart.manualDiscountApplied && cart.manualDiscountApplied !== 0 && cart.discountType === "TD" && typeof processTotalManualDiscount === "function") {
          if (typeof setLoader === "function") setLoader(true);
          setTimeout(async () => {
            await processTotalManualDiscount(cart.manualDiscountApplied);
            if (typeof setLoader === "function") setLoader(false);
          }, 200);
        }
        setCart(updatedCart);
        setShowCustomMix(false);
        setCustomMixItems([]);
        setGroupName("Custom Mix");
        setGroupQty(1);
        setExistingGroupItems(false);
        setShowProductSelection(false);
        setSearchQuery("");
        localStorage.removeItem("customMix");
        localStorage.removeItem("productSegment");
        setCompleteSaleStockData([]);
        setTimeout(() => {
          if (searchInputRef?.current) {
            searchInputRef?.current.focus();
          }
        }, 300);
      }
    } catch (error) {
      // Previously the error was swallowed behind a generic translated string,
      // leaving the cashier staring at an un-dismissed drawer with no clue what
      // failed. Log full context AND surface the real message so it's triageable.
      console.error("[GroupedProducts] Submit error:", error, {
        groupName,
        groupQty,
        customMixItems,
        cartTotal: cart?.total,
      });
      const detail = error?.message || messages.info.submit_error || "Failed to add combo to cart";
      message.error({ content: detail, duration: 6 });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCloseGroupedDrawer = () => {
    setShowCustomMix(false);
    setCustomMixItems([]);
    setGroupName("Custom Mix");
    setShowProductSelection(false);
    setGroupQty(1);
    setSearchQuery("");
    localStorage.removeItem("customMix");
    localStorage.removeItem("productSegment");
    setExistingGroupItems(false);
    setTimeout(() => {
      if (searchInputRef?.current) {
        searchInputRef?.current.focus();
      }
    }, 300);
  };

  return (
    <>
      {/* Previously rendered as centered Modal; switched to right-side Drawer
          to match CustomMixModal (Custom Mix) so the POS keeps a consistent
          "side panel" UX for grouped/combo product entry. */}
      <Drawer
        open={showCustomMix}
        placement="right"
        width="75vw"
        closable={false}
        onClose={handleCloseGroupedDrawer}
        closeIcon={<CloseOutlined style={{ color: "#2F3856", fontSize: "16px" }} />}
        maskClosable={false}
        destroyOnClose
        title={null}
        bodyStyle={{ padding: "30px" }}
      >
  <div
    style={{
      position: "absolute",
      top: "15px",
      right: "20px",
      zIndex: 1000,
      cursor: "pointer",
    }}
    onClick={handleCloseGroupedDrawer}
  >
    <CloseOutlined style={{ color: "#2F3856", fontSize: "18px" }} />
  </div>
        <BarCodeGroupedHandler
          setCustomMixItems={setCustomMixItems}
          cart={cart}
          setCart={setCart}
          setProductStock={setProductStock}
          setShowCompleteSaleStock={setShowCompleteSaleStock}
          setCompleteSaleStockData={setCompleteSaleStockData}
        />
        {showProductSelection ? (
          <div style={{ padding: "5vh 0 0 0", height: "85vh", position: "relative" }}>
            <Input
              placeholder="Search Item Code / Description"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
              style={{
                borderRadius: "7px",
                padding: "0.8vh 0.8vw",
                border: "1px solid #d9d9d9",
                width: "100%",
              }}
            />
            <Row style={{ padding: "2vh 0" }} gutter={[16, 16]}>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const cartItem = customMixItems.find((cartItem) => cartItem.mProductId === item.mProductId && cartItem.uom === (item.uom || item.csUomId));
                  const stdPrecision = item?.uomData?.[0]?.stdprecision ?? 0;
                  const uomName = item?.uomData?.[0]?.name ?? "";
                  const quantity = cartItem ? cartItem.rawWeight || cartItem.weight : "0";
                  const decimal = item?.uomData?.[0]?.decimal === "Y"; // Define decimal here
                  return (
                    <Col key={index} span={12} style={{ padding: "0 8px" }}>
                      <Row style={{ padding: "1.5%", width: "100%", border: "1px solid #d9d9d9", height: "10vh" }}>
                        <Col span={12}>
                          <p
                            title={item.name}
                            style={{
                              margin: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {item.name}
                          </p>
                          <p style={{ margin: 0 }}>
                            {item.sunitprice} - {uomName} {posConfig?.showStoreSpecificStock === "Y" && item.onhandQty !== undefined ? `- ${item.onhandQty ?? 0}` : ""}
                          </p>
                        </Col>
                        <Col span={12} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Button
                              onClick={() => handleDecrease(item)}
                              style={{
                                backgroundColor: "#2F3856",
                                color: "#fff",
                                width: "2.5vw",
                                height: "4vh",
                              }}
                              icon={<MinusOutlined />}
                            />
                            <Input
                              type="text"
                              value={quantity}
                              readOnly={needsPopup(item)}
                              onClick={() => {
                                if (needsPopup(item)) {
                                  openWeightPopupForGrouped(item, async (updatedProduct) => {
                                    const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString();

                                    await handleQuantityChange(item, newValue);
                                  });
                                }
                              }}
                              onKeyDown={(e) => {
                                const decimal = item?.uomData?.[0]?.decimal === "Y";
                                const currentValue = e.target.value;

                                if (currentValue === "0" && /\d/.test(e.key) && !e.target.value.startsWith("0.")) {
                                  e.target.select();
                                }
                                // Prevent multiple decimal points
                                if (decimal && e.key === "." && currentValue.includes(".")) {
                                  e.preventDefault();
                                  return;
                                }

                                // For non-decimal units, allow only digits and control keys
                                if (
                                  !decimal &&
                                  !/[\d]/.test(e.key) &&
                                  e.key !== "Backspace" &&
                                  e.key !== "Delete" &&
                                  e.key !== "ArrowLeft" &&
                                  e.key !== "ArrowRight" &&
                                  e.key !== "Tab"
                                ) {
                                  e.preventDefault();
                                  return;
                                }

                                // For decimal units, allow digits, one decimal point, and control keys
                                if (
                                  decimal &&
                                  !/[\d.]/.test(e.key) &&
                                  e.key !== "Backspace" &&
                                  e.key !== "Delete" &&
                                  e.key !== "ArrowLeft" &&
                                  e.key !== "ArrowRight" &&
                                  e.key !== "Tab"
                                ) {
                                  e.preventDefault();
                                  return;
                                }
                              }}
                              onBlur={(e) => {
                                const decimal = item?.uomData?.[0]?.decimal === "Y";
                                if (decimal && [".", "0.", "0.0", "0.00"].includes(e.target.value)) {
                                  handleQuantityChange(item, "0");
                                }
                              }}
                              placeholder={decimal ? "e.g., 0.001" : "e.g., 1"}
                              style={{
                                width: "60px",
                                height: "4vh",
                                textAlign: "center",
                              }}
                            />
                            <Button
                              onClick={() => handleIncrease(item)}
                              style={{
                                backgroundColor: "#2F3856",
                                color: "#fff",
                                width: "2.5vw",
                                height: "4vh",
                              }}
                              icon={<PlusOutlined />}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  );
                })
              ) : (
                <p style={{ textAlign: "center", width: "100%" }}>No products found</p>
              )}
            </Row>
            <div style={{ position: "absolute", bottom: "0vh", width: "100%", padding: "1vh 0" }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Button
                    disabled={currentPage === 0}
                    onClick={handlePreviousPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: currentPage === 0 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                  >
                    {"<"}
                  </Button>
                  <span style={{ fontWeight: "bold", margin: "0 1vw" }}>
                    Page {currentPage + 1} of {totalPages || 1}
                  </span>
                  <Button
                    disabled={currentPage >= totalPages - 1}
                    onClick={handleNextPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: currentPage >= totalPages - 1 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginLeft: "1vw",
                    }}
                  >
                    {">"}
                  </Button>
                </Col>
                <Col>
                  <Button
                    onClick={() => setShowProductSelection(false)}
                    style={{
                      backgroundColor: "#2F3856",
                      color: "#fff",
                      width: "8vw",
                      height: "6.5vh",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                    disabled={customMixItems.length === 0}
                  >
                    Okay
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <div style={{ height: "81vh", position: "relative" }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: "1vh", marginTop: "3vh" }}>
              <Col>
                <h3>Create Grouped Product</h3>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowProductSelection(true)}
                  style={{ backgroundColor: "#2F3856", borderColor: "#2F3856", color: "#fff" }}
                >
                  Add Products
                </Button>
              </Col>
            </Row>
            <Row align="middle" style={{ marginBottom: "3vh", marginTop: "1vh", marginLeft: "5vw", marginRight: "1vw" }}>
              <Col style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <h3 style={{ marginRight: "7px" }}>Group Name</h3>
                <Input
                  value={groupName}
                  onChange={(e) => {
                    setGroupName(e.target.value);
                  }}
                  style={{ width: "300px", borderRadius: "5px" }}
                />
              </Col>
              <Col style={{ display: "flex", justifyContent: "center", alignItems: "center", marginLeft: "2vw" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "1.1em", fontWeight: 400, color: "#0F0718", marginRight: "8px" }}>Qty:</span>
                  <Button
                    icon={<MinusOutlined style={{ fontSize: "15px" }} />}
                    style={{
                      width: "30px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      paddingRight: 6,
                      marginRight: "15px",
                      paddingLeft: 6,
                      borderRadius: "5px",
                      paddingTop: 0,
                    }}
                    disabled={customMixItems.length === 0 || groupQty <= 1}
                    onClick={() => setGroupQty((prev) => Math.max(prev - 1, 1))}
                  />
                  <Input
                    type="number"
                    min={1}
                    value={groupQty}
                    // onChange={(e) => {
                    //   const value = e.target.value;
                    //   if (value === "" || (!isNaN(value) && Number(value) >= 1)) {
                    //     setGroupQty(value === "" ? 1 : Number(value));
                    //   }
                    // }}
                    onChange={async (e) => {
                      const newGroupQty = Math.max(Number(e.target.value) || 1, 1);
                      const prevGroupQty = groupQty;

                      const updatedItems = customMixItems.map((item) => {
                        const perItemQty = parseFloat(item.weight || 1);
                        const totalQty = parseFloat((perItemQty * newGroupQty).toFixed(item?.uomData?.[0]?.stdprecision ?? 2));

                        return {
                          ...item,
                          weight: totalQty,
                          rawWeight: perItemQty,
                        };
                      });

                      const tempCart = buildMergedCartForStock(updatedItems);

                      const isPreOrderPage = window.location.pathname.includes("/preorder");

                      if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
                        const stockCheckResult = await stock({
                          cart: tempCart,
                          setCart,
                          tillData,
                          setProductStock,
                          isFinalValidation: false,
                          isCompleteSale: true,
                          setShowCompleteSaleStock,
                          setCompleteSaleStockData,
                        });

                        if (!stockCheckResult) {
                          setGroupQty(prevGroupQty);
                          return;
                        }
                      }

                      setGroupQty(newGroupQty);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === "e" || e.key === "-" || e.key === "+") {
                        e.preventDefault();
                      }
                    }}
                    onBlur={(e) => {
                      const value = Number(e.target.value) || 1;
                      setGroupQty(Math.max(value, 1));
                    }}
                    style={{ width: "70px", textAlign: "center" }}
                    disabled={customMixItems.length === 0}
                  />
                  <Button
                    icon={<PlusOutlined style={{ fontSize: "15px", color: "#fff" }} />}
                    style={{
                      width: "30px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      paddingRight: 6,
                      marginLeft: "15px",
                      paddingLeft: 6,
                      backgroundColor: "#2F3856",
                      borderRadius: "5px",
                    }}
                    disabled={(!groupName.trim() && customMixItems.length === 0) || customMixItems.length === 0}
                    // onClick={() => {
                    //   setGroupQty((prev) => prev + 1);
                    // }}
                    onClick={async () => {
                      const newGroupQty = groupQty + 1;
                      const prevGroupQty = groupQty;
                      const updatedItems = customMixItems.map((item) => {
                        const perItemQty = parseFloat(item.weight);
                        const totalQty = parseFloat((perItemQty * newGroupQty).toFixed(item?.uomData?.[0]?.stdprecision ?? 2));

                        return {
                          ...item,
                          weight: totalQty,
                          rawWeight: perItemQty,
                        };
                      });

                      const tempCart = buildMergedCartForStock(updatedItems);
                      const isPreOrderPage = window.location.pathname.includes("/preorder");
                      if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
                        const stockCheckResult = await stock({
                          cart: tempCart,
                          setCart,
                          tillData,
                          setProductStock,
                          isFinalValidation: false,
                          isCompleteSale: true,
                          setShowCompleteSaleStock,
                          setCompleteSaleStockData,
                        });
                        if (!stockCheckResult) {
                          setGroupQty(prevGroupQty);
                          return;
                        }
                      }
                      setGroupQty(newGroupQty);
                    }}
                  />
                </div>
              </Col>
            </Row>
            <Row style={{ padding: "0.1rem 0.75rem", fontSize: "0.75rem", fontWeight: 600, color: "#2F3856", borderBottom: "1px solid #e8e8e8", marginBottom: "0.5rem" }}>
              <Col span={6}>Name</Col>
              <Col span={4} style={{ textAlign: "right" }}>
                Unit Price
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Qty
              </Col>
              <Col span={2} style={{ textAlign: "right" }}>
                Total Qty
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Total Price
              </Col>
              <Col span={2}></Col>
            </Row>
            <Scrollbars style={{ height: "54vh" }}>
              {customMixItems?.map((item) => {
                const unitPrice = parseFloat(item.sunitprice) || 0;
                const quantity = parseFloat((item.weight * (groupQty || 1)).toFixed(precision)) || 0;
                const totalPrice = parseFloat((unitPrice * quantity).toFixed(precision));
                const decimal = item?.uomData?.[0]?.decimal === "Y"; // Define decimal here
                return (
                  <Row
                    key={item.id || item.name}
                    style={{
                      padding: "0.5rem 0.9rem",
                      width: "100%",
                      marginBottom: "0.4rem",
                      height: "auto",
                      alignItems: "center",
                    }}
                  >
                    <Col span={6}>
                      <p
                        title={item.name}
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "#333",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                      >
                        {item.name}
                      </p>
                    </Col>
                    <Col span={4} style={{ textAlign: "right" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          color: "#2F3856",
                        }}
                      >
                        {unitPrice.toLocaleString("en-IN")}
                      </p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <Input
                        type="text"
                        value={item.rawWeight || item.weight}
                        readOnly={needsPopup(item)}
                        onClick={() => {
                          if (needsPopup(item)) {
                            openWeightPopupForGrouped(item, async (updatedProduct) => {
                              const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString();

                              await handleQuantityChange(item, newValue);
                            });
                          }
                        }}
                        onChange={(e) => handleQuantityChange(item, e.target.value)}
                        onKeyDown={(e) => {
                          const decimal = item?.uomData?.[0]?.decimal === "Y";
                          const currentValue = e.target.value;

                          if (currentValue === "0" && /\d/.test(e.key) && !e.target.value.startsWith("0.")) {
                            e.target.select();
                          }
                          // Prevent multiple decimal points
                          if (decimal && e.key === "." && currentValue.includes(".")) {
                            e.preventDefault();
                            return;
                          }

                          // For non-decimal units, allow only digits and control keys
                          if (
                            !decimal &&
                            !/[\d]/.test(e.key) &&
                            e.key !== "Backspace" &&
                            e.key !== "Delete" &&
                            e.key !== "ArrowLeft" &&
                            e.key !== "ArrowRight" &&
                            e.key !== "Tab"
                          ) {
                            e.preventDefault();
                            return;
                          }

                          // For decimal units, allow digits, one decimal point, and control keys
                          if (
                            decimal &&
                            !/[\d.]/.test(e.key) &&
                            e.key !== "Backspace" &&
                            e.key !== "Delete" &&
                            e.key !== "ArrowLeft" &&
                            e.key !== "ArrowRight" &&
                            e.key !== "Tab"
                          ) {
                            e.preventDefault();
                            return;
                          }
                        }}
                        onBlur={(e) => {
                          const decimal = item?.uomData?.[0]?.decimal === "Y";
                          if (decimal && [".", "0.", "0.0", "0.00"].includes(e.target.value)) {
                            handleQuantityChange(item, "0"); // Reset partial decimals to 0
                          }
                        }}
                        placeholder={decimal ? "e.g., 0.001" : "e.g., 1"}
                        style={{
                          width: "60px",
                          height: "25px",
                          textAlign: "center",
                          fontSize: "0.8rem",
                        }}
                      />
                    </Col>
                    <Col span={2} style={{ textAlign: "right" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          color: "#2F3856",
                        }}
                      >
                        {quantity}
                      </p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          color: "#2F3856",
                        }}
                      >
                        {totalPrice.toLocaleString("en-IN")}
                      </p>
                    </Col>
                    <Col span={2} style={{ textAlign: "center" }}>
                      <img
                        src={DeleteIcon}
                        style={{
                          height: "2.5vh",
                          cursor: "pointer",
                        }}
                        onClick={() => handleDelete(item)}
                      />
                    </Col>
                  </Row>
                );
              })}
            </Scrollbars>
            <Button
              style={{
                position: "absolute",
                bottom: "0.5rem",
                right: "0.5rem",
                backgroundColor: "#2F3856",
                color: "#fff",
                width: "7vw",
                height: "4.5vh",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                fontSize: "0.85rem",
              }}
              disabled={submitting}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        )}
      </Drawer>
      {/* Integrated weight popup -- for products WITHOUT alternate UOM (ProductsPopup-style) */}
      <IntegratedWeightModal
        open={integratedWeightPopupOpen && !(integratedWeightProduct?.alternateUom?.length > 0 && integratedWeightProduct?.showAlternateUomInPos === "Y")}
        product={integratedWeightProduct}
        onTotalQty={handleIntegratedWeightTotalQty}
        onInputChange={handleIntegratedWeightInputChange}
        onConfirm={handleIntegratedWeightConfirm}
        onCancel={handleIntegratedWeightCancel}
        inputRef={integratedWeightInputRef}
        strictQty={posConfig?.requireQtyBeforeAddingItem === "Y"}
      />
    </>
  );
};

export default GroupedProducts;
